import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = ["data-level"]

import { type PropType, computed, onMounted, ref, watch } from "vue";
import Item from "./item.vue";
import {
    iterateAllItemsFlattened,
    type PatchedMenuItem,
} from "./menu-item";
import FadeAndSlide from "../../shared/FadeAndSlide.vue";
import labels from "../../shared/labels";
import { type CSSProperties } from "vue/types/jsx";
import { type FocusElement, useFocus } from "../../../../state/focus";
import { ensureUniqueKey } from "../../../../shared/unique-identifier-key";
const showParentItemLink = false;
// const showParentItemLink = computed(
//     () =>
//         !props.showAsColumn &&
//         !props.onePerColumnLayout &&
//         props.dropdown &&
//         hasLink(props.parentItem)
// );


export default /*@__PURE__*/_defineComponent({
  __name: 'items',
  props: {
    menuItems: {
        type: Array as PropType<PatchedMenuItem[]>,
        required: true,
    },
    parentItem: Object as PropType<PatchedMenuItem>,
    mobile: Boolean,
    dropdown: Boolean,
    parentLast: Boolean,
    showAsColumn: Boolean,
    level: {
        type: Number,
        required: true,
    },
    onePerColumnLayout: Boolean,
},
  setup(__props) {

const props = __props;

const focusStore = useFocus();

function getMenuUniqueKeyFromFocus(
    currentFocus: (typeof focusStore)["currentFocus"]
) {
    if (currentFocus == null) return undefined;
    if (currentFocus.key.indexOf("menu-item_") !== 0) return undefined;

    return currentFocus.key.substring("menu-item_".length);
}

function isExpanded(
    menuItem: PatchedMenuItem,
    currentFocus: string | (typeof focusStore)["currentFocus"]
): boolean {
    if (typeof currentFocus != "string") {
        if (currentFocus == null) return false;

        currentFocus = getMenuUniqueKeyFromFocus(currentFocus);

        if (currentFocus == null) return false;
    }

    const key = ensureUniqueKey(menuItem);

    if (currentFocus == key) {
        return true;
    }

    if (menuItem.items == null || menuItem.items.length === 0) return false;

    return menuItem.items.some((x) => isExpanded(x, currentFocus));
}

function getFriendlyName(menuItem: PatchedMenuItem): string {
    if (menuItem.parent == null) {
        return menuItem.name;
    }
    return getFriendlyName(menuItem.parent) + " > " + menuItem.name;
}

function findParentMenuItem(
    currentFocus: FocusElement
): FocusElement | undefined {
    const key = getMenuUniqueKeyFromFocus(currentFocus);

    for (const item of iterateAllItemsFlattened(props.menuItems[0])) {
        if (ensureUniqueKey(item) === key) {
            if (item.parent == null) break;
            return createFocusElement(item.parent);
        }
    }
    return undefined;
}

function createFocusElement(menuItem: PatchedMenuItem): FocusElement {
    return {
        parent: findParentMenuItem,
        friendlyName: getFriendlyName(menuItem),
        key: "menu-item_" + ensureUniqueKey(menuItem),
    };
}

function expandMenuItem(menuItem: PatchedMenuItem) {
    focusStore.set(createFocusElement(menuItem));
}

function collapseMenuItem(menuItem: PatchedMenuItem) {
    const previousFocus = focusStore.currentFocus;
    const uniqueKeyBefore = getMenuUniqueKeyFromFocus(previousFocus);

    if (uniqueKeyBefore == ensureUniqueKey(menuItem)) {
        focusStore.blurCurrent();
    } else {
        focusStore.set(createFocusElement(menuItem));
        focusStore.blurCurrent();
    }
}

const showAll = ref(false);

const limitCountDefault = computed(() => (props.mobile ? 5 : 15));

const limitItems = computed(() => {
    return (
        ((props.mobile && props.dropdown) ||
            (!props.mobile && props.showAsColumn)) &&
        props.menuItems.length > limitCountDefault.value
    );
});

const limit = computed(() => {
    if (!limitItems.value) {
        return 0;
    }
    return showAll.value ? 0 : limitCountDefault.value;
});

const el = ref<HTMLUListElement>();

const manualRows = computed(() => {
    if (!props.dropdown || props.mobile) return false;

    const cnt = props.menuItems.length;
    if (cnt < 9) return Math.ceil(cnt / 2);
    return Math.ceil(cnt / 3);
});

const styleObj = ref<CSSProperties>({});

function updateStyleValue(rows: false | number) {
    //console.log("setting rows", rows, el.value);
    if (rows !== false) {
        const value = `repeat(${rows}, 1fr)`;
        if (el.value != null) el.value.style.gridTemplateRows = value;
        styleObj.value["grid-template-rows"] = value;
    } else {
        if (el.value != null) el.value.style.gridTemplateRows = ``;
        styleObj.value["grid-template-rows"] = undefined;
    }
}

watch(
    () => manualRows.value,
    (rows) => {
        updateStyleValue(rows);
    },
    {
        immediate: true,
    }
);

const childItemsAndIndexes = computed(() => {
    return props.menuItems
        .map((menuItem, i) => ({
            menuItem,
            i,
        }))
        .filter(
            (x) =>
                props.dropdown == false ||
                props.onePerColumnLayout == false ||
                !(
                    x.menuItem.specialAttributes != null &&
                    "dontShowAsColumn" in x.menuItem.specialAttributes &&
                    x.menuItem.specialAttributes.dontShowAsColumn === "true"
                )
        )
        .flatMap((x) => {
            if (props.mobile && x.menuItem.transparentOnMobile === true) {
                return (
                    x.menuItem.items?.map((y, i) => ({ i: i, menuItem: y })) ??
                    []
                );
            }
            return [x];
        })
        .map((x, i) => ({ ...x, n: i }));
});

onMounted(() => {
    updateStyleValue(manualRows.value);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("ul", {
    class: _normalizeClass([
            _ctx.$style['menu-list'],
            __props.mobile ? _ctx.$style['mobile-menu-list'] : _ctx.$style['desktop-menu-list'],
            __props.showAsColumn ? _ctx.$style['column-list'] : '',
            __props.dropdown ? _ctx.$style['dropdown'] : '',
            __props.onePerColumnLayout ? _ctx.$style['one-per-column'] : '',
        ]),
    "data-level": __props.level,
    ref_key: "el",
    ref: el,
    style: _normalizeStyle(styleObj.value)
  }, [
    showParentItemLink
      ? (_openBlock(), _createBlock(Item, {
          key: 0,
          level: __props.level,
          mobile: __props.mobile,
          "dropdown-item": true,
          "menu-item": __props.parentItem,
          "disable-items": "",
          first: "",
          last: __props.parentLast && __props.menuItems.length == 0 && !limitItems.value
        }, null, 8 /* PROPS */, ["level", "mobile", "menu-item", "last"]))
      : _createCommentVNode("v-if", true),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(childItemsAndIndexes.value, ({ menuItem, i, n }) => {
      return (_openBlock(), _createElementBlock(_Fragment, null, [
        (n > 0 && __props.onePerColumnLayout)
          ? (_openBlock(), _createElementBlock("li", {
              key: 0,
              class: _normalizeClass(_ctx.$style['vertical-line'])
            }, null, 2 /* CLASS */))
          : _createCommentVNode("v-if", true),
        _createVNode(FadeAndSlide, null, {
          default: _withCtx(() => [
            (limit.value === 0 || n < limit.value)
              ? (_openBlock(), _createBlock(Item, {
                  level: __props.level,
                  mobile: __props.mobile,
                  "dropdown-item": __props.dropdown,
                  "column-item": __props.showAsColumn,
                  "show-sub-items-in-column": __props.onePerColumnLayout,
                  expanded: isExpanded(menuItem, _unref(focusStore).currentFocus),
                  onExpand: ($event: any) => (expandMenuItem(menuItem)),
                  onCollapse: ($event: any) => (collapseMenuItem(menuItem)),
                  "menu-item": menuItem,
                  key: i,
                  first: i === 0 && !showParentItemLink,
                  last: 
                        __props.parentLast && i === __props.menuItems.length - 1 && !limitItems.value
                    
                }, null, 8 /* PROPS */, ["level", "mobile", "dropdown-item", "column-item", "show-sub-items-in-column", "expanded", "onExpand", "onCollapse", "menu-item", "first", "last"]))
              : _createCommentVNode("v-if", true)
          ], undefined, true),
          _: 2 /* DYNAMIC */
        }, 1024 /* DYNAMIC_SLOTS */)
      ], 64 /* STABLE_FRAGMENT */))
    }), 256 /* UNKEYED_FRAGMENT */)),
    (limitItems.value)
      ? (_openBlock(), _createBlock(Item, {
          level: __props.level,
          pseudo: "",
          mobile: __props.mobile,
          "dropdown-item": __props.dropdown,
          "column-item": __props.showAsColumn,
          name: showAll.value ? _unref(labels).topSite.showLess : _unref(labels).topSite.showMore,
          key: 'showMore',
          bold: true,
          smaller: __props.mobile,
          last: __props.parentLast,
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showAll.value = !showAll.value))
        }, null, 8 /* PROPS */, ["level", "mobile", "dropdown-item", "column-item", "name", "smaller", "last"]))
      : _createCommentVNode("v-if", true)
  ], 14 /* CLASS, STYLE, PROPS */, _hoisted_1))
}
}

})